import BaseScatterChartWidgetController from './base_scatter_chart_widget_controller';
import { PURPLE_500, RED_500 } from '../../../shared/colors';

export default class extends BaseScatterChartWidgetController {
  static values = {
    ...BaseScatterChartWidgetController.values,
    xAxisLabel: String,
    actualProgressLabel: String,
    completionRateLabel: String,
  };

  static targets = [
    ...BaseScatterChartWidgetController.targets,
    'metricSelect',
  ];

  connect() {
    super.connect();
    this.currentMetric = 'actual_progress';
    this.lastData = null;
  }

  changeMetric(event) {
    this.currentMetric = event.target.value;
    if (this.lastData) {
      this.renderChart(this.lastData);
    }
  }

  renderData(data) {
    this.lastData = data;
    super.renderData(data);
  }

  getScatterConfig(data) {
    const { programs, trend_line } = data;
    if (!programs || programs.length === 0) return null;

    const yKey = this.currentMetric;
    const yLabel =
      yKey === 'actual_progress'
        ? this.actualProgressLabelValue
        : this.completionRateLabelValue;

    const scatterPoints = programs.map((p) => ({
      x: p.social_interactions,
      y: p[yKey],
      name: p.name,
      id: p.id,
    }));

    const xMax = Math.max(...scatterPoints.map((p) => p.x), 1);
    const yMax = Math.max(...scatterPoints.map((p) => p.y), 1);

    const datasets = [
      {
        label: 'Programs',
        data: scatterPoints,
        backgroundColor: PURPLE_500,
        borderColor: PURPLE_500,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ];

    if (trend_line) {
      const trendLineData = this.calculateTrendLine(scatterPoints);
      if (trendLineData) {
        datasets.push({
          type: 'line',
          label: 'Trend',
          data: trendLineData,
          borderColor: RED_500,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
        });
      }
    }

    return {
      datasets,
      xLabel: this.xAxisLabelValue,
      yLabel,
      xMax,
      yMax,
    };
  }

  calculateTrendLine(points) {
    if (points.length < 2) return null;

    const xValues = points.map((p) => p.x);
    const yValues = points.map((p) => p.y);

    const n = xValues.length;
    const sumX = xValues.reduce((a, b) => a + b, 0);
    const sumY = yValues.reduce((a, b) => a + b, 0);
    const sumXY = xValues.reduce((acc, x, i) => acc + x * yValues[i], 0);
    const sumX2 = xValues.reduce((acc, x) => acc + x * x, 0);

    const denominator = n * sumX2 - sumX * sumX;
    if (denominator === 0) return null;

    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);

    return [
      { x: minX, y: Math.max(0, slope * minX + intercept) },
      { x: maxX, y: Math.max(0, slope * maxX + intercept) },
    ];
  }

  formatTooltipLabel(context) {
    const point = context.raw;
    if (point.name) {
      const metricLabel =
        this.currentMetric === 'actual_progress'
          ? this.actualProgressLabelValue
          : this.completionRateLabelValue;
      return [
        point.name,
        `${this.xAxisLabelValue}: ${this.formatNumber(point.x)}`,
        `${metricLabel}: ${point.y}%`,
      ];
    }
    return `${this.formatNumber(point.x)}, ${point.y}%`;
  }
}
